import React from 'react'
import { motion } from 'framer-motion'
import { Play, Pause, Heart, MoreHorizontal } from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'

const PlaylistView = ({ playlist, currentTrack, onTrackSelect }) => {
  const { 
    isPlaying,
    isFavorite,
    addFavorite,
    removeFavorite
  } = useMusic()

  const formatDuration = (duration) => {
    if (!duration) return '--:--'
    
    const minutes = Math.floor(duration / 60)
    const seconds = Math.floor(duration % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const toggleFavorite = (e, track) => {
    e.stopPropagation() // 防止触发播放
    
    if (isFavorite(track.id)) {
      removeFavorite(track.id)
    } else {
      addFavorite(track)
    }
  }

  if (!playlist || playlist.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">播放列表</h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">🎵</div>
          <p className="text-gray-500">暂无播放列表</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          播放列表
        </h3>
        <span className="text-sm text-gray-500">
          {playlist.length} 首歌曲
        </span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {playlist.map((track, index) => {
          const isCurrentTrack = currentTrack && track.id === currentTrack.id
          const isTrackPlaying = isCurrentTrack && isPlaying

          return (
            <motion.div
              key={track.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => onTrackSelect(track)}
              className={`
                group flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200
                ${isCurrentTrack 
                  ? 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200' 
                  : 'hover:bg-gray-50'
                }
              `}
            >
              {/* 序号/播放状态 */}
              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                {isCurrentTrack ? (
                  <motion.div
                    animate={{ scale: isTrackPlaying ? [1, 1.2, 1] : 1 }}
                    transition={{ repeat: isTrackPlaying ? Infinity : 0, duration: 1 }}
                  >
                    {isTrackPlaying ? (
                      <Pause className="w-4 h-4 text-purple-600" />
                    ) : (
                      <Play className="w-4 h-4 text-purple-600" />
                    )}
                  </motion.div>
                ) : (
                  <span className="text-sm text-gray-500 group-hover:hidden">
                    {index + 1}
                  </span>
                )}
                
                {!isCurrentTrack && (
                  <Play className="w-4 h-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>

              {/* 封面 */}
              <div className="flex-shrink-0">
                <img
                  src={track.artwork || '/default-cover.jpg'}
                  alt={track.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              </div>

              {/* 歌曲信息 */}
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium truncate ${
                  isCurrentTrack ? 'text-purple-700' : 'text-gray-900'
                }`}>
                  {track.title}
                </h4>
                <p className="text-sm text-gray-600 truncate">
                  {track.artist}
                </p>
              </div>

              {/* 时长 */}
              <div className="flex-shrink-0 text-sm text-gray-500">
                {formatDuration(track.duration)}
              </div>

              {/* 操作按钮 */}
              <div className="flex-shrink-0 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => toggleFavorite(e, track)}
                  className={`p-1 rounded-full transition-colors ${
                    isFavorite(track.id)
                      ? 'text-red-500 bg-red-50'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart 
                    className="w-4 h-4" 
                    fill={isFavorite(track.id) ? 'currentColor' : 'none'} 
                  />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    // 这里可以添加更多操作的菜单
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* 播放列表统计 */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            总时长: {formatDuration(
              playlist.reduce((total, track) => total + (track.duration || 0), 0)
            )}
          </span>
          <span>
            收藏: {playlist.filter(track => isFavorite(track.id)).length} 首
          </span>
        </div>
      </div>
    </motion.div>
  )
}

export default PlaylistView
